import { API_BASE_URL, ApiError } from './client';
import { ValidationFailure } from './events';
import type { FieldErrors } from './events';

/**
 * One piece of the troop's property on one event.
 *
 * `holderPersonId` is null when nobody has been given it yet, and that is a real state rather than
 * a row to hide — it is the troop's, it is going, and on paper it is the line the quartermaster
 * writes a name onto at the trailer.
 */
export interface GearItem {
  itemId: string;
  listId: string;
  /** Unit | Subgroup | Personal — which list it is sitting on. */
  level: string;
  text: string;
  category: string | null;
  quantity: number;
  notes: string | null;
  subgroupId: string | null;
  /** The group's own name, rendered as-is. The unit's word for a patrol is composed where it is drawn. */
  subgroupName: string | null;
  holderPersonId: string | null;
  holderName: string | null;
  /** Returned | Damaged | Missing, or null while nobody has said. Never a boolean. */
  returned: string | null;
  returnNote: string | null;
  returnedRecordedAt: string | null;
}

/**
 * What the unit has out on one trip.
 *
 * `outstanding` is "nobody has said" plus `Missing`, and pointedly not everything unreturned: a
 * stove back with a broken zip is on the trailer, and counting it as out would give a leader a
 * number that reads worse than the truth and points at nothing.
 */
export interface GearRegister {
  total: number;
  outstanding: number;
  items: GearItem[];
}

/** One thing that did not simply come back. */
export interface GearException {
  itemId: string;
  state: 'Returned' | 'Damaged' | 'Missing';
  note: string | null;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  if (!response.ok) {
    let problem: { title?: string; detail?: string; errors?: FieldErrors } = {};
    try {
      problem = await response.json();
    } catch {
      // Non-JSON body; the status is enough.
    }

    if (response.status === 400 && problem.errors) {
      throw new ValidationFailure(problem.errors);
    }

    throw new ApiError(
      response.status,
      problem.detail ?? problem.title ?? `Failed (${response.status}).`,
    );
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const gearApi = {
  /**
   * The register. 404 covers "no such event here" and "not allowed" alike — that a trip exists is
   * the event's own disclosure to make, and it has already made it.
   */
  register: (unitId: string, eventId: string) =>
    call<GearRegister>(`/units/${unitId}/events/${eventId}/gear/`),

  /**
   * Closes the trip out. The body is the **exceptions and nothing else**: everything the leader did
   * not name is recorded as returned, so an empty list is the all-clear and is the common case.
   */
  closeOut: (unitId: string, eventId: string, exceptions: GearException[]) =>
    call<{ recorded: number }>(`/units/${unitId}/events/${eventId}/gear/close-out`, {
      method: 'POST',
      body: JSON.stringify({ exceptions }),
    }),

  /** One line, corrected — the stove that turns up in a garage the following Thursday. */
  setReturned: (unitId: string, eventId: string, exception: GearException) =>
    call<void>(
      `/units/${unitId}/events/${eventId}/gear/items/${exception.itemId}/returned`,
      { method: 'PUT', body: JSON.stringify(exception) },
    ),

  /**
   * Marks a line as the troop's, or takes that mark off. Hangs off the **list** rather than the
   * event, because it is legal on a template — which is how the unit's standard gear list pre-marks
   * the trailer for every trip published from it.
   */
  setUnitOwned: (unitId: string, listId: string, itemId: string, isUnitOwned: boolean) =>
    call<unknown>(`/units/${unitId}/packing-lists/${listId}/items/${itemId}/unit-owned`, {
      method: 'PUT',
      body: JSON.stringify({ isUnitOwned }),
    }),
};
